import { useEffect, useState } from 'react';

/**
 * useNarrowViewport reports whether the viewport is at or below breakpointPx.
 *
 * This is the ONLY client-side input to the render shape (decision #5057). The
 * server owns which shape applies below which width; the browser evaluates the
 * media query and nothing else, because viewport width is only knowable here.
 *
 * Viewport, not container: a bar that collapses to an icon has a small box by
 * nature, so a container-keyed rule would latch -- once collapsed it would never
 * expand back, and under some layouts it would oscillate.
 *
 * Without matchMedia (SSR, or a very old browser) the answer is false, so the
 * BASE shape renders. That direction is deliberate: showing a desktop control on
 * a phone is a worse outcome than the reverse, but shipping a phone control to
 * every desktop is worse still, and it is what a true default would do.
 *
 * @param {number|null} breakpointPx - Max viewport width in CSS px, or null
 *   when the instance declares no collapse
 * @returns {boolean} Whether the narrow shape applies
 *
 * @example
 * const isNarrow = useNarrowViewport(narrowShape ? narrowBreakpointFor(config) : null);
 */
export function useNarrowViewport(breakpointPx) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (!breakpointPx || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setMatches(false);
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${breakpointPx}px)`);
    setMatches(mediaQuery.matches);

    const handleChange = (event) => setMatches(event.matches);

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy Safari (before 14) exposes only the deprecated pair. The widget
    // runs on customer sites, so it cannot assume the modern API.
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [breakpointPx]);

  return matches;
}
